import { isDefined } from "@clipboard-health/util-ts";
import { type BaseChannel } from "@sendbird/chat";
import { type GroupChannel, GroupChannelHandler } from "@sendbird/chat/groupChannel";
import sendbirdSelectors from "@sendbird/uikit-react/sendbirdSelectors";
// eslint-disable-next-line import/no-named-as-default
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlags } from "@src/appV2/FeatureFlags/useCbhFlags";
import { addAppStateChangeListener } from "@src/appV2/NativeAdapters/app.adapter";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import { GET_UPCOMING_SHIFTS_GROUPED_URL } from "../api/useGetUpcomingShiftsGrouped";
import { GET_CHAT_CHANNELS_QUERY_KEY } from "../constants";

/**
 * Registers Sendbird event handlers that invalidate React Query caches
 * when channels change, messages are received, or users are invited.
 * Also handles app foreground/background state transitions.
 *
 * This hook has no return value — it only produces side effects.
 * Mount it once inside the ChatProvider tree.
 */
export function useChatEventHandlers(): void {
  const queryClient = useQueryClient();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(sendBirdState);
  const ldFlags = useCbhFlags();
  const isSendBirdOptimisationEnabled =
    ldFlags[CbhFeatureFlag.SENDBIRD_CONCURRENT_CONNECTION_OPTIMISATION];

  // Register Sendbird event handlers for real-time updates
  useEffect(() => {
    if (!isDefined(sdk?.groupChannel)) {
      return undefined;
    }

    const handlerId = uuidv4();

    const channelHandler = new GroupChannelHandler({
      onChannelChanged: () => {
        void queryClient.invalidateQueries(GET_CHAT_CHANNELS_QUERY_KEY);
      },
      onMessageReceived: (_channel: BaseChannel, message) => {
        if (message.isAdminMessage()) {
          void queryClient.invalidateQueries([GET_UPCOMING_SHIFTS_GROUPED_URL]);
        }
      },
    });

    channelHandler.onUserReceivedInvitation = (_channel: GroupChannel) => {
      void queryClient.invalidateQueries(GET_CHAT_CHANNELS_QUERY_KEY);
      void queryClient.invalidateQueries([GET_UPCOMING_SHIFTS_GROUPED_URL]);
    };

    sdk.groupChannel.addGroupChannelHandler(handlerId, channelHandler);

    return () => {
      sdk?.groupChannel.removeGroupChannelHandler?.(handlerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk]);

  // Handle app foreground/background state for Sendbird connection
  useEffect(() => {
    if (!isSendBirdOptimisationEnabled) {
      return () => undefined;
    }

    const listenerPromise = addAppStateChangeListener(({ isActive }) => {
      if (isActive && sdk?.connectionState === "CLOSED") {
        sdk?.setForegroundState?.();
        void queryClient.invalidateQueries(GET_CHAT_CHANNELS_QUERY_KEY);
        void queryClient.invalidateQueries([GET_UPCOMING_SHIFTS_GROUPED_URL]);
      }

      if (!isActive && sdk?.connectionState !== "CLOSED") {
        sdk?.setBackgroundState?.();
      }
    });

    return () => {
      void listenerPromise.then((handle) => handle.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk]);
}
